import React, { useRef, useState } from 'react';
import { COUNTRY_ISO } from '../constants';

const tagBase = {
  fontSize: 13, padding: '3px 10px', borderRadius: 20,
  display: 'inline-flex', alignItems: 'center',
  userSelect: 'none', cursor: 'pointer',
};

const s = {
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  rowMobile: { display: 'flex', flexDirection: 'column', gap: 10 },
  box: (over, borderColor) => ({
    border: `1px solid ${over ? borderColor : '#d1d5db'}`,
    borderRadius: 8, overflow: 'hidden',
    transition: 'border-color 0.15s',
  }),
  header: (color) => ({
    padding: '6px 10px',
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    fontSize: 12, fontWeight: 600, color,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  }),
  body: {
    display: 'flex', flexWrap: 'wrap', gap: 6,
    padding: '8px 10px', minHeight: 52,
  },
  tagIncluded: { ...tagBase, background: '#eff6ff', color: '#1d4ed8' },
  tagExcluded: { ...tagBase, background: '#f1f5f9', color: '#64748b' },
  empty: { fontSize: 12, color: '#94a3b8', padding: '6px 2px' },
  btn: (color) => ({
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 11, color, fontWeight: 500, padding: 0,
  }),
};

export default function CountryTransfer({ value, onChange, isMobile }) {
  const [overInclude, setOverInclude] = useState(false);
  const [overExclude, setOverExclude] = useState(false);
  const dragging = useRef(null);

  const included = new Set(value || []);
  const includedList = COUNTRY_ISO.filter(({ code }) => included.has(code));
  const excludedList = COUNTRY_ISO.filter(({ code }) => !included.has(code));

  function include(code) { onChange([...(value || []), code]); }
  function exclude(code) { onChange((value || []).filter((c) => c !== code)); }

  function onDragStart(e, code, from) {
    dragging.current = { code, from };
    e.dataTransfer.effectAllowed = 'move';
  }
  function onDragEnd() {
    dragging.current = null;
    setOverInclude(false);
    setOverExclude(false);
  }
  function onDrop(target) {
    if (!dragging.current) return;
    const { code, from } = dragging.current;
    if (from !== target) target === 'include' ? include(code) : exclude(code);
  }

  function Tag({ code, from }) {
    const isDragging = dragging.current?.code === code;
    const style = from === 'include' ? s.tagIncluded : s.tagExcluded;
    return (
      <span
        style={{ ...style, opacity: isDragging ? 0.4 : 1 }}
        draggable
        onDragStart={(e) => onDragStart(e, code, from)}
        onDragEnd={onDragEnd}
        onClick={() => { if (!isDragging) from === 'include' ? exclude(code) : include(code); }}
        title={from === 'include' ? 'Click or drag to exclude' : 'Click or drag to include'}
      >
        {code}
      </span>
    );
  }

  return (
    <div style={isMobile ? s.rowMobile : s.row}>
      {/* Included — left */}
      <div
        style={s.box(overInclude, '#2563eb')}
        onDragOver={(e) => { e.preventDefault(); setOverInclude(true); }}
        onDragLeave={() => setOverInclude(false)}
        onDrop={() => { onDrop('include'); setOverInclude(false); }}
      >
        <div style={s.header('#1d4ed8')}>
          <span>Included ({includedList.length})</span>
          <button style={s.btn('#2563eb')} type="button" onClick={() => onChange(COUNTRY_ISO.map((c) => c.code))}>
            add all
          </button>
        </div>
        <div style={s.body}>
          {includedList.length === 0
            ? <span style={s.empty}>Drag or click countries to include</span>
            : includedList.map(({ code }) => <Tag key={code} code={code} from="include" />)
          }
        </div>
      </div>

      {/* Excluded — right */}
      <div
        style={s.box(overExclude, '#94a3b8')}
        onDragOver={(e) => { e.preventDefault(); setOverExclude(true); }}
        onDragLeave={() => setOverExclude(false)}
        onDrop={() => { onDrop('exclude'); setOverExclude(false); }}
      >
        <div style={s.header('#64748b')}>
          <span>Excluded ({excludedList.length})</span>
          <button style={s.btn('#dc2626')} type="button" onClick={() => onChange([])}>
            exclude all
          </button>
        </div>
        <div style={s.body}>
          {excludedList.length === 0
            ? <span style={s.empty}>All countries included</span>
            : excludedList.map(({ code }) => <Tag key={code} code={code} from="exclude" />)
          }
        </div>
      </div>
    </div>
  );
}
